import type { ContextInput } from '@innspel/core';

/**
 * The technical context on the web — K5 is a closed allow-list, and on the web it is
 * FIVE lines (tech-decisions.md, «M1.5»): `screen` is the URL path without query
 * string and hash (both can carry tokens), `os` is the browser with version
 * (the contract: «På web: nettleser»), `locale` from the browser, `sdkVersion` from
 * the core, and `appVersion`/`build` from the host's config. There is no viewport.
 *
 * Never the raw user-agent string: it is a fingerprint (K20). Only family and
 * major version, like «Chrome 129».
 */
export function browserName(ua: string): string | undefined {
  const treff =
    /Edg\/(\d+)/.exec(ua)?.[1] !== undefined
      ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}`
      : /OPR\/(\d+)/.exec(ua)
        ? `Opera ${/OPR\/(\d+)/.exec(ua)![1]}`
        : /Firefox\/(\d+)/.exec(ua)
          ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}`
          : /Chrome\/(\d+)/.exec(ua)
            ? `Chrome ${/Chrome\/(\d+)/.exec(ua)![1]}`
            : /Version\/(\d+).*Safari/.exec(ua)
              ? `Safari ${/Version\/(\d+).*Safari/.exec(ua)![1]}`
              : undefined;
  return treff;
}

/** The path alone. Query string and hash are stripped — they can carry tokens. */
export function pagePath(url: string): string {
  try {
    return new URL(url, 'http://x').pathname || '/';
  } catch {
    return '/';
  }
}

/**
 * The context as `init()` expects it. `platform` is always `web`; the host adds
 * `appVersion`/`build`. `screen` is set per opening of the widget
 * (`open({ screen })`) and not here — the path may have changed since `init`.
 */
export function webContext(
  vert: { appVersion?: string; build?: string } = {},
  miljø: { userAgent?: string; language?: string } = typeof navigator === 'undefined' ? {} : navigator,
): ContextInput {
  const ut: ContextInput = { platform: 'web' };
  const os = miljø.userAgent ? browserName(miljø.userAgent) : undefined;
  if (os) ut.os = os;
  if (miljø.language) ut.locale = miljø.language;
  if (vert.appVersion) ut.appVersion = vert.appVersion;
  if (vert.build) ut.build = vert.build;
  return ut;
}
